import { giphyBlack, giphyDarkGrey, giphyIndigo } from '@giphy/colors'
import { ReactNode, useContext } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { BORDER_RADIUS_MEDIUM } from '../constants'
import { CreationToolsContext } from '../manager'
import { getBasePathFromCreationTool } from '../routes'
import CreationToolError from '../types/error'
import CreationToolErrorFix from '../types/error-fix'
import CreationToolOutputType from '../types/output-type'
import CreationToolStep from '../types/steps'
import { UtilityButton } from './buttons'
import { Text, Title } from './typography'

const Content = styled.div`
    background: ${giphyBlack};
    border-radius: ${BORDER_RADIUS_MEDIUM}px;
    box-sizing: content-box;
    padding: 50px 40px;
    position: relative;
    text-align: center;
`

const Sticker = styled.img`
    display: block;
    margin: 0 auto 10px;
`

const Buttons = styled.div`
    display: flex;
    justify-content: center;
    margin: 30px auto 0;
    width: 300px;
`

const Button = styled(UtilityButton)`
    background: ${giphyDarkGrey};
    flex-shrink: 0;
    margin-right: 20px;
    padding: 0 15px;

    &:last-child {
        background: ${giphyIndigo};
        flex-shrink: 1;
        margin-right: 0;
        width: 100%;
    }
`

type Props = {
    className?: string
    error: CreationToolError
    sourceId?: string
    stickerDisplayMode?: 'none' | 'small' | 'large'
    goBackLabel?: string
    onGoBack: () => void
    onChangeOutputType?: (outputType: CreationToolOutputType) => void
    onRetry?: () => void
}

const CreationToolsErrorModal = ({
    className,
    error,
    sourceId,
    stickerDisplayMode = 'large',
    goBackLabel,
    onGoBack,
    onChangeOutputType,
    onRetry,
}: Props) => {
    const { tool, userRestrictionAccess } = useContext(CreationToolsContext)

    const title = CreationToolError.title(error)
    const description = CreationToolError.description(error)
    const errorFix = CreationToolError.fix(error)
    const stickerId = stickerDisplayMode !== 'none' && CreationToolError.stickerId(error)

    const errorFixRestriction = CreationToolErrorFix.userRestriction(errorFix) // get error fix user restrictions
    let errorFixOutputType = CreationToolErrorFix.outputType(errorFix) // get error fix output type
    const canAccessErrorFix = userRestrictionAccess.indexOf(errorFixRestriction) > -1 // check if user can access fix
    let fix = errorFix

    // check fix permissions and fallback to support center
    if (!canAccessErrorFix) {
        fix = CreationToolErrorFix.SupportCenter
    }

    // check if alternative output type but no change listener
    // and fallback to support center
    if (errorFixOutputType && !onChangeOutputType) {
        errorFixOutputType = null
        fix = CreationToolErrorFix.SupportCenter
    }

    // check if is retry and retry handler
    if (fix === CreationToolErrorFix.RetryUpload && !onRetry) {
        fix = CreationToolErrorFix.SupportCenter
    }

    const fixUrl = CreationToolErrorFix.url(fix)
    const fixStep = CreationToolErrorFix.step(fix)
    const fixTitle = CreationToolErrorFix.title(fix)
    const fixDescription = CreationToolErrorFix.description(fix)

    const getButton = (): ReactNode => {
        // change output type fix
        if (errorFixOutputType && onChangeOutputType) {
            return <Button onClick={() => onChangeOutputType(errorFixOutputType!)}>{fixTitle}</Button>
        }
        // retry fix
        if (fix === CreationToolErrorFix.RetryUpload && onRetry) {
            return <Button onClick={onRetry}>{fixTitle}</Button>
        }
        // step redirect fix
        if (fixStep) {
            return (
                <Button
                    as={Link}
                    to={{
                        pathname: `${getBasePathFromCreationTool(tool)}${CreationToolStep.route(fixStep)}`,
                        state: { sourceId },
                    }}
                >
                    {fixTitle}
                </Button>
            )
        }
        return (
            <Button
                {...{
                    as: 'a',
                    href: fixUrl,
                    rel: 'noreferrer noopener',
                    target: '_blank',
                }}
            >
                {fixTitle}
            </Button>
        )
    }

    return (
        <Content className={className}>
            {stickerId && (
                <Sticker
                    src={`//media.giphy.com/media/${stickerId}/200.gif`}
                    alt=""
                    style={{
                        height: stickerDisplayMode === 'small' ? 60 : 100,
                        marginTop: stickerDisplayMode === 'small' ? -30 : -100,
                    }}
                />
            )}
            <Title>{title}</Title>
            {description && (
                <Text>
                    {description} {fixDescription}
                </Text>
            )}
            <Buttons>
                <Button onClick={onGoBack}>{goBackLabel || `Go Back`}</Button>
                {getButton()}
            </Buttons>
        </Content>
    )
}

export default CreationToolsErrorModal
